import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Snackbar, Alert, Button } from '@mui/material';
import { onQueryFailure } from '@/api/queryClient';

/**
 * The last line of defence against a silently failed request.
 *
 * Mounted once, at the root. Every query and mutation that fails without its
 * own handler reaches here, so the app can no longer render "No posts yet" when
 * what actually happened is that the request failed — which the audit found to
 * be the behaviour of 69 of 71 queries.
 *
 * Deliberately a Snackbar and not a modal. A page that half-loaded is usually
 * still useful, and stopping the person to acknowledge a problem they cannot
 * fix would be a worse experience than the silence it replaces. It states what
 * happened, offers one button that actually helps, and gets out of the way.
 */
export default function QueryFailureBanner() {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    /*
     * Only the first failure is shown until it is dismissed.
     *
     * A dropped connection fails every query on the screen at once — the
     * dashboard alone has five. Replacing the message each time would flicker
     * through them and reset the auto-hide timer on every one, so the person
     * sees a stuttering bar for as long as the outage lasts.
     */
    return onQueryFailure((next) => setFailure((current) => current ?? next));
  }, []);

  const retry = useCallback(async () => {
    setRetrying(true);

    try {
      // Only what is on screen. Refetching the whole cache would hammer the
      // server on the way back from an outage, which is when it can least
      // afford it.
      await queryClient.refetchQueries({ type: 'active' });
      setFailure(null);
    } finally {
      setRetrying(false);
    }
  }, [queryClient]);

  return (
    <Snackbar
      open={!!failure}
      autoHideDuration={8000}
      onClose={(_, reason) => {
        // Not on clickaway: this is information, and dismissing it by
        // accidentally clicking the page is how it gets missed.
        if (reason !== 'clickaway') setFailure(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert
        severity="error"
        variant="filled"
        onClose={() => setFailure(null)}
        sx={{ alignItems: 'center' }}
        action={
          <Button color="inherit" size="small" disabled={retrying} onClick={retry}>
            {retrying ? 'Retrying…' : 'Retry'}
          </Button>
        }
      >
        {failure?.message}
      </Alert>
    </Snackbar>
  );
}
